/**
 * Wanneer er een adresverzoek uitgaat terwijl iemand typt.
 *
 * ------------------------------------------------------------------
 * Twee regels, en geen van beide is uit de code te lezen zonder er een
 * typsnelheid bij te halen. Daarom staan ze hier apart met tests eronder,
 * zelfde afweging als `ophaalTempo.js` — en met dezelfde getallen als
 * `android/.../data/RitTempo.kt`, want twee clients die hetzelfde veld anders
 * afremmen is één functie die zich op twee telefoons anders gedraagt.
 *
 * **Niet bij elke toetsaanslag.** Achter dit veld zit PDOK, een
 * overheidsdienst, met een verzoeklimiet van negentig per minuut. Wie
 * "Coolsingel Rotterdam" uittypt doet negentien aanslagen; ongeremd zijn dat
 * negentien verzoeken voor één bestemming. Zie [zonderRem], die dat uitrekent
 * in plaats van het te beweren.
 *
 * **En niets onder de twee letters.** Op "a" geeft PDOK heel Nederland terug:
 * zeven suggesties die niets met je bestemming te maken hebben, precies op het
 * moment dat je nog niets hebt getypt om op te kiezen.
 *
 * ------------------------------------------------------------------
 * **Waarom dit geen `moetOphalen` uit ophaalTempo.js is**, al lijkt dat de
 * plek. Dat is een throttle op de vóórste rand: de eerste vraag mag door en de
 * rest wordt geweigerd tot de klok om is. Voor een bron die je elke tien
 * seconden pollt is dat precies goed. Voor een veld waarin iemand typt is het
 * verkeerd om: je stuurt "Co" weg en "Coolsingel" nooit — de eerste letters in
 * plaats van de laatste. Een invoerveld wil de áchterste rand: even niets
 * doen, dan het laatste wat er staat.
 *
 * `latestOnly()` hoort hier wél bij en wordt niet nagebouwd: dat is de regel
 * dat alleen het nieuwste antwoord mag schrijven, en die staat al in
 * `latestOnly.js`. Op Android heet dat `RitTempo.magSchrijven`; hier was het er
 * al, dus staat het hier niet.
 */

/**
 * Hoe lang het stil moet zijn voordat er iets uitgaat, in milliseconden.
 *
 * Bij vier aanslagen per seconde — een gewone typist — is 300 ms het verschil
 * tussen één verzoek per woord en één per letter. Lager en je stuurt halve
 * woorden weg; hoger en het veld voelt alsof het niet reageert.
 */
export const RUST_MS = 300;

/** Minder letters dan dit en er gaat niets uit. */
export const MIN_LETTERS = 2;

/**
 * Of deze invoer de moeite van een verzoek waard is.
 *
 * Spaties tellen niet mee: " a " is één letter, en wie net een woord heeft
 * gewist houdt spaties over waar hij niets meer zoekt.
 */
export const magZoeken = (invoer) =>
  String(invoer || "").trim().length >= MIN_LETTERS;

/**
 * Hoeveel verzoeken dit woord ongeremd zou kosten.
 *
 * Eén per aanslag vanaf de tweede letter. Staat hier zodat de test het getal
 * uitrekent in plaats van dat er een bewering in een opmerking staat —
 * hetzelfde als `verzoekenPerUur` in ophaalTempo.js.
 */
export const zonderRem = (tekst) =>
  Math.max(0, String(tekst || "").trim().length - MIN_LETTERS + 1);
